import uniq from "lodash-es/uniq";
import { Action, Reducer } from "redux";

import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { errorMessage } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingState } from "@/mk2/reducers/loadingState";

export interface State {
    ids: number[];
    loadingState: LoadingState;
    hasNext: number;
    hasPrevious: number;
    errorMessage: string;
}

export const initialState: State = {
    ids: [],
    loadingState: LoadingState.INIT,
    hasNext: null,
    hasPrevious: null,
    errorMessage: null,
};

export interface InfiniteListAction extends Action {
    readonly after: number;
}
export interface InfiniteListRequestAction extends InfiniteListAction, APIRequestAction {}
export interface InfiniteListFailureAction extends InfiniteListAction, APIFailureAction {}
export interface InfiniteListSuccessAction extends InfiniteListAction, APISuccessAction {
    readonly hasNext: number;
    readonly hasPrevious: number;
}

const defaultResultSelector = (action) => action.response.result;

const reducerFactory =
    <
        TRequestAction extends InfiniteListRequestAction,
        TSuccessAction extends InfiniteListSuccessAction,
        TFailureAction extends InfiniteListFailureAction,
    >(
        apiType: string,
        resultSelector: (action: TSuccessAction) => number[] = defaultResultSelector,
    ): Reducer<State> =>
    (state = initialState, action: TRequestAction | TSuccessAction | TFailureAction) => {
        switch (action.type) {
            case `${apiType}_REQUEST`:
            case `${apiType}_FAILURE`:
                return {
                    ...state,
                    loadingState: loadingState(state.loadingState, action),
                    errorMessage: errorMessage(state.errorMessage, action),
                };
            case `${apiType}_SUCCESS`:
                const typedAction = action as TSuccessAction;
                let ids = [...state.ids];
                const index = ids.indexOf(Math.abs(typedAction.after));
                const result = resultSelector(typedAction);
                if (index === -1) {
                    // Not found in the list
                    ids =
                        typedAction.after < 0 || !typedAction.after
                            ? // Prepend
                              [
                                  ...result,
                                  ...(ids.length && ids[0] === typedAction.hasNext ? ids : []),
                              ]
                            : // Append
                              [
                                  ...(ids.length && ids[ids.length - 1] === typedAction.hasPrevious
                                      ? ids
                                      : []),
                                  ...result,
                              ];
                } else {
                    // Found in the list
                    ids =
                        typedAction.after < 0
                            ? [...ids.slice(0, index), ...result, ...ids.slice(index)]
                            : [...ids.slice(0, index + 1), ...result, ...ids.slice(index + 1)];
                }

                const first = result[0];
                const last = result[result.length - 1];

                // Make ids unique
                ids = uniq(ids);

                return {
                    ...state,
                    ids,
                    loadingState: loadingState(state.loadingState, typedAction),
                    errorMessage: errorMessage(state.errorMessage, typedAction),
                    hasNext: last === ids[ids.length - 1] ? typedAction.hasNext : state.hasNext,
                    hasPrevious: first === ids[0] ? typedAction.hasPrevious : state.hasPrevious,
                };
            default:
                return state;
        }
    };

export default reducerFactory;
